import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { BarChart3, ArrowDown, ChevronDown, ChevronRight, Info } from "lucide-react";
import { pct, ConfidenceIndicator } from "./KPICard";

// DASH-EMAIL-P1-HIGHINTENT-001 Slice 4: the six mutually exhaustive High
// Intent business-action drilldown buckets, in the exact order/labels the
// Overview row expands to. Keyed by the SAME field names GET /lpe-
// funnel's high_intent_actions.breakdown returns (see
// useEmailDashboardData.js's own HIGH_INTENT_BREAKDOWN_KEYS) -- never a
// second, independently-maintained key list.
//
// DASH-EMAIL-P1-HIGHINTENT-001 Slice 4A: `description` is business-
// friendly UI copy only -- approved customer-facing wording, never a
// canonical event name, PRG002/governed-program reference, attribution
// implementation detail, internal route id, or database term. Surfaced
// via a small hover/focus info affordance per row (see the info-icon
// Tooltip below), never as permanently-visible paragraph text.
const HIGH_INTENT_BREAKDOWN_LABELS = [
  {
    key: "online_enrollment_actions",
    label: "Online Enrollment Actions",
    description: "Customer chose to continue toward an online application from Quote Results or the Agent Page.",
  },
  {
    key: "continue_online_actions",
    label: "Continue Online Actions",
    description:
      "Customer chose Continue Online from a qualified nurture email and reached the controlled Continue Online handoff.",
  },
  {
    key: "speak_to_an_agent_actions",
    label: "Speak to an Agent Actions",
    description:
      "Customer chose agent assistance from Quote Results or from a qualified nurture email and reached the Agent Page.",
  },
  {
    key: "call_agent_actions",
    label: "Call Agent Actions",
    description: "Customer initiated a phone call from the Agent Page.",
  },
  {
    key: "schedule_agent_actions",
    label: "Schedule Agent Actions",
    description: "Customer initiated the schedule-a-call action from the Agent Page.",
  },
  {
    key: "save_quote_submissions",
    label: "Save Quote Submissions",
    description: "Customer completed the Save Quote action from Quote Results.",
  },
];

const HIGH_INTENT_STAGE_LABEL = "High Intent Actions";

/**
 * EmailFunnelCard — Checkpoint 3.1 acquisition funnel visualization.
 *
 * Mirrors the existing proportional-width-bar pattern established by
 * FunnelVisualization.jsx (same Card, bar, and connector treatment) rather
 * than introducing a new charting approach. Shape is
 * {label, value, status}[], matching the Email data contract, so
 * conversion-from-previous is derived here for display only rather than
 * carried as separate fields.
 *
 * DASH-EMAIL-008: `value` may now be `null`/`undefined` for a genuinely
 * unavailable stage (no source system exists for it — see
 * emailDashboardMockAdapter.js's buildFunnel()) as well as for the
 * Delivered stage specifically when its real fetch failed
 * (useEmailDashboardData.js never falls back to a fabricated number for
 * it). This component must render such a stage honestly: an em dash in
 * place of the count, no proportional bar fill (never a width computed
 * from a non-numeric value), and no "% of prev" conversion badge for any
 * transition where either side is unavailable — never NaN, never a
 * fabricated 0%.
 *
 * DASH-EMAIL-008B: a real value of exactly 0 (e.g. Delivered = 0) must
 * render a literal 0% fill, never the same visible minimum-width fill a
 * positive value gets for legibility — a floored "partial-looking" bar for
 * a true zero is itself a fabricated signal. The 4%-minimum floor below
 * therefore only ever applies to a genuinely positive value.
 *
 * DASH-EMAIL-P1-IMPL-004: DASH-EMAIL-P1-IMPL-002 briefly gave each stage a
 * `periodScope` (Delivered was "all_time", the four LPE stages were
 * "selected_period") and this component disclosed/suppressed conversions
 * across that mismatch. As of this checkpoint Delivered ALSO honors the
 * selected Period (see useEmailDashboardData.js's own header comment) --
 * every stage now shares one reporting interval, so that distinction and
 * its suppression logic have been removed; a "% of prev" conversion badge
 * is shown whenever both sides of a transition have a real numeric value,
 * full stop.
 *
 * DASH-EMAIL-P1-IMPL-008: this component previously accepted `stage.status`
 * but never read it — a fabricated/"estimated" stage (Performance's mock
 * funnel, Audience's mock Lifecycle Distribution funnel) rendered
 * pixel-identical to a genuinely real one, with zero visual disclosure.
 * Now renders the SAME ConfidenceIndicator (icon + tooltip) KPICard already
 * uses for `status !== "exact"` next to each stage's label — reused
 * directly (KPICard.jsx now exports it) rather than reimplemented, so
 * every "estimated"/"missing_data"/etc stage anywhere this component is
 * reused is honestly, consistently disclosed. Renders nothing extra for
 * "exact" (or an absent) status — Overview's and Performance's now-real
 * stages are unaffected, matching KPICard's own "quiet unless something is
 * off" convention (no added noise).
 *
 * DASH-EMAIL-P1-HIGHINTENT-001 Slice 4: an optional `highIntentBreakdown`
 * prop (the six mutually exhaustive business-action bucket counts, see
 * HIGH_INTENT_BREAKDOWN_LABELS above) makes the "High Intent Actions" row
 * specifically clickable/expandable, revealing its six buckets beneath it
 * as visual children (indented, connected by a left border, never a
 * modal). Collapsed by default. This is entirely additive and OPT-IN per
 * caller: when `highIntentBreakdown` is omitted/null (e.g. Performance's
 * own `<EmailFunnelCard funnel={funnel} />` call, which has no breakdown
 * data to offer), the High Intent Actions row renders exactly as it did
 * before this checkpoint -- plain, non-interactive, no chevron. The six
 * child rows never render a "% of prev" conversion badge or any other
 * funnel-stage vocabulary -- they are independent action counts, not
 * additional funnel stages, matching the standalone Actions view's own
 * established "these are not a funnel" discipline.
 *
 * DASH-EMAIL-P1-HIGHINTENT-001 Slice 4A: each of the six bucket rows
 * carries a small adjacent info icon (reusing the shared Radix-backed
 * Tooltip primitive from ui/tooltip.jsx -- the same one KPICard.jsx's
 * ConfidenceIndicator already uses, never a second tooltip framework)
 * that reveals approved, business-friendly copy on hover or keyboard
 * focus -- never permanently-visible paragraph text, never shifting the
 * card's own layout (Radix renders tooltip content into a portal). This
 * is presentation-only: it does not read any new prop, touch metric
 * values, or affect expand/collapse behavior in any way.
 *
 * EMAIL-DASHBOARD-KPI-001: the optional `methodologyDisclosure` prop moves
 * the funnel's own persistent methodology paragraph (previously a
 * permanent block above this card in EmailOverviewView.jsx) into an info
 * icon beside this card's own "Acquisition Funnel" heading -- same Tooltip
 * primitive/hover+focus behavior as the six High Intent bucket rows below,
 * never a bespoke second tooltip system. Text is passed through verbatim
 * (no truncation/rewrite here). Omitting the prop renders the heading
 * exactly as before this checkpoint, with no icon at all -- purely
 * additive, matching this component's own established optional-prop
 * convention (`highIntentBreakdown` above).
 */
export default function EmailFunnelCard({ funnel, highIntentBreakdown = null, methodologyDisclosure = null }) {
  const [highIntentExpanded, setHighIntentExpanded] = useState(false);

  if (!funnel || funnel.length === 0) return null;

  const maxValue = Math.max(...funnel.map((s) => (typeof s.value === "number" ? s.value : 0)), 1);

  return (
    <Card className="glass border-white/10" data-testid="email-funnel-card">
      <CardHeader>
        <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-indigo-400" />
          Acquisition Funnel
          {methodologyDisclosure && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex text-slate-500 hover:text-slate-300 cursor-help"
                    aria-label="About the Acquisition Funnel"
                    data-testid="email-funnel-methodology-info"
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  className="max-w-md text-xs"
                  data-testid="email-funnel-methodology-tooltip"
                >
                  {methodologyDisclosure}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {funnel.map((stage, idx) => {
            const hasValue = typeof stage.value === "number";
            // DASH-EMAIL-008B: the 4% legibility floor applies ONLY to a
            // genuinely positive value -- a true zero must render exactly
            // 0% width, never a minimum-width fill that would visually
            // misrepresent it as having some (unspecified) positive
            // volume.
            const width = hasValue && stage.value > 0 ? Math.max((stage.value / maxValue) * 100, 4) : 0;
            const prevStage = idx > 0 ? funnel[idx - 1] : null;
            const prevHasValue = !!prevStage && typeof prevStage.value === "number";
            const convRate = hasValue && prevHasValue && prevStage.value ? stage.value / prevStage.value : null;

            const isHighIntent = stage.label === HIGH_INTENT_STAGE_LABEL;
            const isExpandable = isHighIntent && !!highIntentBreakdown;

            return (
              <div key={stage.label} className="space-y-1" data-testid={`email-funnel-stage-${stage.label}`}>
                <div className="flex items-center justify-between text-sm">
                  {isExpandable ? (
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-slate-200 hover:text-slate-100"
                      onClick={() => setHighIntentExpanded((prev) => !prev)}
                      aria-expanded={highIntentExpanded}
                      data-testid="email-funnel-high-intent-toggle"
                    >
                      {/* EMAIL-DASHBOARD-KPI-002: theme-indigo treatment
                          (text-indigo-400 -- the same accent already used
                          by this card's own BarChart3 heading icon and the
                          funnel bars' gradient) for stronger visual
                          prominence. Presentation only -- expand/collapse
                          and rotation/orientation behavior are unchanged. */}
                      {highIntentExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 text-indigo-400" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-indigo-400" />
                      )}
                      {stage.label}
                      <ConfidenceIndicator status={stage.status} reason={stage.reason} />
                    </button>
                  ) : (
                    <span className="flex items-center gap-1.5 text-slate-200">
                      {stage.label}
                      <ConfidenceIndicator status={stage.status} reason={stage.reason} />
                    </span>
                  )}
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-slate-100">
                      {hasValue ? stage.value.toLocaleString() : "—"}
                    </span>
                    {convRate !== null && (
                      <Badge className="bg-indigo-500/20 text-indigo-300 text-xs">
                        {pct(convRate)} of prev
                      </Badge>
                    )}
                  </div>
                </div>
                <div
                  className="h-8 w-full rounded-lg bg-white/5 overflow-hidden"
                  data-testid={`email-funnel-stage-${stage.label}-track`}
                >
                  {width > 0 && (
                    <div
                      className="h-full rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
                      style={{ width: `${width}%` }}
                      data-testid={`email-funnel-stage-${stage.label}-fill`}
                    />
                  )}
                </div>
                {isExpandable && highIntentExpanded && (
                  <div
                    className="ml-4 mt-2 space-y-2 border-l border-white/10 pl-4"
                    data-testid="email-funnel-high-intent-breakdown"
                  >
                    {HIGH_INTENT_BREAKDOWN_LABELS.map(({ key, label, description }) => {
                      const bucketValue = highIntentBreakdown[key];
                      const bucketHasValue = typeof bucketValue === "number";
                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between text-xs"
                          data-testid={`email-funnel-high-intent-bucket-${key}`}
                        >
                          {/* EMAIL-DASHBOARD-KPI-002: theme-indigo label
                              text (same text-indigo-400 accent as the
                              funnel heading icon/bars and the parent High
                              Intent caret above) for stronger scan-ability
                              of the six buying-intent categories -- counts
                              deliberately stay their existing slate tone
                              (see the sibling span below), and indentation/
                              row structure/tooltip behavior are unchanged. */}
                          <span className="flex items-center gap-1 text-indigo-400">
                            {label}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex text-slate-500 hover:text-slate-300 cursor-help"
                                    aria-label={`About ${label}`}
                                    data-testid={`email-funnel-high-intent-bucket-${key}-info`}
                                  >
                                    <Info className="h-3 w-3" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs text-xs">
                                  {description}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </span>
                          <span className="font-mono text-slate-300">
                            {bucketHasValue ? bucketValue.toLocaleString() : "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {idx < funnel.length - 1 && (
                  <div className="flex justify-center py-1">
                    {/* EMAIL-DASHBOARD-KPI-002: theme-indigo treatment
                        (text-indigo-400 -- the same accent already used by
                        this card's own BarChart3 heading icon) for stronger
                        visual prominence between stages. Presentation
                        only -- stage labels/counts/bars/calculations/
                        spacing are unchanged. */}
                    <ArrowDown className="h-4 w-4 text-indigo-400" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
